import {
  DataBlockVariant,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import Logger from 'js-logger';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Field } from '../common/Field';
import { Loading } from '../common/Loading';
import { DropDownInput } from '../common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import {
  dataBlockVariantsForDataBlockFieldsPage,
  sharedTypeNames,
  useDataBlockTypeTranslation,
  visitInformationDataBlockTypeName,
} from '../measurements/common';
import { DataBlockFieldsTable } from './components/DataBlockFieldsTable';

import styles from './DataBlockFields.module.scss';

export const DataBlockFields: FC = React.memo(function DataBlockFields() {
  const { queryParams, setQueryParams } = pages.dataBlockFields.useParams();
  const dataBlockTypeId = queryParams.typeId;
  const dataBlockVariant = queryParams.variant;
  const i18next = useTranslation();
  const forms = useGlobalForms();
  const queryClient = useQueryClient();

  const measurementTypes = useEnabledDataBlockTypes();
  const translateDataBlockType = useDataBlockTypeTranslation();

  const filteredDataBlockTypes = useMemo(() => {
    return measurementTypes.filter(
      (type) =>
        type.variant === dataBlockVariant &&
        !(
          type.variant === DataBlockVariant.Visit &&
          type.value === visitInformationDataBlockTypeName
        ) &&
        !(
          type.variant === DataBlockVariant.SharedFields &&
          type.value === sharedTypeNames.file
        ),
    );
  }, [dataBlockVariant, measurementTypes]);

  const fieldsQuery =
    QueryFactory.DataBlockFieldQuery.useGetDataBlockFieldsForEditingQuery(
      dataBlockTypeId!,
      {
        enabled: !!dataBlockTypeId,
      },
    );

  useEffect(() => {
    const updateSelectedDataBlockType = async () => {
      if (dataBlockVariant) {
        if (filteredDataBlockTypes.length > 0) {
          const dataBlockTypeToSelect = filteredDataBlockTypes.find(
            (type) => type.id === dataBlockTypeId,
          );
          if (dataBlockTypeToSelect) {
            setQueryParams({ typeId: dataBlockTypeToSelect.id });
          } else {
            setQueryParams({ typeId: filteredDataBlockTypes[0].id });
          }
          // } else {
          //   // we should not reset dataBlockTypeId, because filteredDataBlockTypes are not yet loaded, but we might have some dataBlockType already selected.
          //   setDataBlockTypeId(undefined);
        }
      } else {
        setQueryParams({ variant: DataBlockVariant.DeviceMeasurement });
      }
    };
    updateSelectedDataBlockType().catch((e) => Logger.error(e));
  }, [filteredDataBlockTypes, dataBlockTypeId]);

  useEffect(() => {
    if (dataBlockTypeId) {
      const updateFields = async () => {
        if (dataBlockTypeId) {
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.DataBlockFieldQuery.getDataBlockFieldsForEditingQueryKey(
                dataBlockTypeId,
              ),
          });
        }
      };
      updateFields().catch((e) => Logger.error(e));
    }
  }, [dataBlockTypeId]);

  const dataBlockVariantSelectControl = (
    <div className={styles.typeSelection}>
      <Field
        title={i18next.t('data_blocks.variant')}
        className={styles.filterField}
      >
        <DropDownInput
          useIdFunctionAsValue={true}
          options={dataBlockVariantsForDataBlockFieldsPage.map((variant) => {
            return {
              value: variant,
              displayValue: i18next.t(`data_block_type.${variant}`),
            };
          })}
          idFunction={'value'}
          value={dataBlockVariant}
          getOptionLabel={'displayValue'}
          onValueChanged={(option) => {
            setQueryParams({ variant: option.value });
          }}
          required={true}
        />
      </Field>
    </div>
  );

  const selectDataBlockType = dataBlockVariant && (
    <div className={styles.typeSelection}>
      <Field
        title={i18next.t('data_blocks.type')}
        linkProps={
          dataBlockVariant !== DataBlockVariant.SharedFields
            ? {
                title: i18next.t('data_blocks.add_button'),
                onClick: () =>
                  forms.openDataBlockTypeCreation(
                    dataBlockVariant,
                    (newDataBlockTypeId) => {
                      setQueryParams({ typeId: newDataBlockTypeId });
                    },
                  ),
                className: styles.edit,
              }
            : undefined
        }
        className={styles.filterField}
      >
        <DropDownInput
          useIdFunctionAsValue={true}
          options={filteredDataBlockTypes}
          idFunction={'id'}
          value={dataBlockTypeId}
          getOptionLabel={translateDataBlockType}
          onValueChanged={(selectedMeasurementType) => {
            setQueryParams({ typeId: selectedMeasurementType.id });
          }}
          required={true}
        />
      </Field>
    </div>
  );
  const dataBlockType = filteredDataBlockTypes.find(
    (type) => type.id === dataBlockTypeId,
  );
  return (
    <div className={styles.storedScrolling}>
      <Loading
        title={i18next.t('data_blocks.loading')}
        loading={fieldsQuery.isLoading}
        testId="data-block-fields-page"
      >
        <div className={styles.container}>
          <div className={styles.settingsContainer}>
            {dataBlockVariantSelectControl}
            {selectDataBlockType}
          </div>
          {dataBlockType && (
            <DataBlockFieldsTable
              dataBlockType={dataBlockType}
              dataBlockFields={fieldsQuery.data ?? []}
            />
          )}
        </div>
      </Loading>
    </div>
  );
});
